import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import DashboardCard from '../../components/DashboardCard';
import StatusBadge from '../../components/StatusBadge';

const pad = (n) => String(n).padStart(2, '0');

function buildHours(byHour) {
  const hours = Array.from({ length: 24 }, (_, h) => ({ label: pad(h), count: 0 }));
  (byHour || []).forEach(({ hour, count }) => {
    if (hour >= 0 && hour <= 23) hours[hour].count = count;
  });
  return hours;
}

function buildDays(byDay, days) {
  const list = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    list.push({ key, label: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`, count: 0 });
  }
  (byDay || []).forEach(({ day, count }) => {
    const item = list.find((x) => x.key === day);
    if (item) item.count = count;
  });
  return list;
}

function LineChart({ data, height = 240, labelEvery = 1, showValues = false }) {
  const canvasRef = useRef(null);
  const [w, setW] = useState(0);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const update = () => setW(el.clientWidth);
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const n = data.length;
  const max = Math.max(...data.map((d) => d.count), 1);
  const padL = 42;
  const padR = 16;
  const padT = 30;
  const padB = 34;
  const innerW = Math.max((w || 720) - padL - padR, 10);
  const innerH = height - padT - padB;
  const xAt = (i) => (n <= 1 ? padL + innerW / 2 : padL + (i * innerW) / (n - 1));
  const yAt = (c) => padT + innerH - (c / max) * innerH;
  const pts = data.map((d, i) => `${xAt(i)},${yAt(d.count)}`);
  const hasData = data.some((d) => d.count > 0);
  const peakIdx = data.reduce((best, d, i) => (d.count > data[best].count ? i : best), 0);
  const step = Math.max(labelEvery, Math.ceil((n * 46) / innerW));
  const showAllValues = showValues && innerW / n >= 32;
  const gid = `lcFill${n}_${height}`;
  const mid = max >= 2 && max % 2 === 0 ? max / 2 : null;
  const yTicks = mid !== null ? [max, mid, 0] : [max, 0];

  return (
    <div className="chart-wrap">
      <div className="chart-canvas" ref={canvasRef}>
        {w > 0 && (
          <svg className="line-chart" width={w} height={height} viewBox={`0 0 ${w} ${height}`}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" className="lc-grad-1" />
                <stop offset="100%" className="lc-grad-2" />
              </linearGradient>
            </defs>
            {yTicks.map((v) => (
              <g key={v}>
                <line className="lc-grid" x1={padL} y1={yAt(v)} x2={w - padR} y2={yAt(v)} />
                <text className="lc-ylabel" x={padL - 8} y={yAt(v) + 4} textAnchor="end">{v}</text>
              </g>
            ))}
            <polygon
              fill={`url(#${gid})`}
              points={`${padL},${yAt(0)} ${pts.join(' ')} ${w - padR},${yAt(0)}`}
            />
            <polyline className="lc-line" points={pts.join(' ')} />
            {data.map((d, i) => (
              <g key={d.label + i}>
                <circle
                  cx={xAt(i)}
                  cy={yAt(d.count)}
                  r={i === peakIdx && hasData ? 5 : 3}
                  className={`lc-dot${i === peakIdx && hasData ? ' peak' : ''}`}
                >
                  <title>{`${d.label}: ${d.count} pesquisa${d.count === 1 ? '' : 's'}`}</title>
                </circle>
                {d.count > 0 && (showAllValues || i === peakIdx) && (
                  <text
                    className={`lc-value${i === peakIdx && hasData ? ' peak' : ''}`}
                    x={xAt(i)}
                    y={yAt(d.count) - 11}
                    textAnchor="middle"
                  >
                    {d.count}
                  </text>
                )}
              </g>
            ))}
            {data.map((d, i) => (
              i % step === 0 && (
                <text key={'lbl' + i} className="lc-label" x={xAt(i)} y={height - 10} textAnchor="middle">
                  {d.label}
                </text>
              )
            ))}
          </svg>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [searches, setSearches] = useState([]);
  const [chart, setChart] = useState(null);
  const [visitsChart, setVisitsChart] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/recent-searches'),
      api.get('/admin/search-chart', { params: { days: 14 } }),
      api.get('/admin/visits-chart', { params: { days: 14 } }),
    ]).then(([statsRes, searchesRes, chartRes, visitsRes]) => {
      setStats(statsRes.data);
      setSearches(searchesRes.data);
      setChart(chartRes.data);
      setVisitsChart(visitsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="empty-state"><h3>Carregando...</h3></div>;
  }

  const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const hours = buildHours(chart?.byHour);
  const days = buildDays(chart?.byDay, chart?.days || 14);
  const visitDays = buildDays(visitsChart?.byDay, visitsChart?.days || 14);
  const hourTotal = hours.reduce((s, h) => s + h.count, 0);
  const dayTotal = days.reduce((s, d) => s + d.count, 0);
  const visitTotal = visitDays.reduce((s, d) => s + d.count, 0);
  const peakHour = hours.reduce((a, b) => (b.count > a.count ? b : a), hours[0]);
  const peakDay = days.reduce((a, b) => (b.count > a.count ? b : a), days[0]);
  const peakVisitDay = visitDays.reduce((a, b) => (b.count > a.count ? b : a), visitDays[0]);

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Visão geral do localizador de jazigos.</p>
      </div>

      <div className="dashboard-grid">
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V7a7 7 0 0 1 14 0v14"></path><path d="M3 21h18"></path></svg>}
          label="Jazigos ocupados"
          value={stats?.graves_occupied || 0}
          trend={`de ${stats?.graves_total || 0} no total`}
        />
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>}
          label="Jazigos com GPS"
          value={stats?.graves_with_gps || 0}
          trend={
            stats && stats.graves_total > 0
              ? `${Math.round((Number(stats.graves_with_gps) / Number(stats.graves_total)) * 100)}% do total`
              : 'nenhum capturado ainda'
          }
        />
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>}
          label="Sepultados"
          value={stats?.deceased_total || 0}
          trend={`em ${stats?.graves_with_deceased || 0} jazigos`}
        />
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>}
          label="Visitas hoje"
          value={stats?.visits_today || 0}
          trend={`${stats?.visits_month || 0} no mês — landing page`}
        />
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>}
          label="Pesquisas hoje"
          value={stats?.searches_today || 0}
        />
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>}
          label="Pesquisas no mês"
          value={stats?.searches_month || 0}
        />
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>}
          label="Leads captados"
          value={stats?.leads_month || 0}
        />
        <DashboardCard
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>}
          label="Jazigos encontrados"
          value={stats?.found_month || 0}
        />
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3>Pesquisas por horário</h3>
          {hourTotal > 0 && peakHour.count > 0 && (
            <span className="chart-peak">Pico: {peakHour.label}h — {peakHour.count} pesquisa{peakHour.count === 1 ? '' : 's'}</span>
          )}
        </div>
        {hourTotal === 0 ? (
          <p className="chart-empty">Nenhuma pesquisa registrada ainda.</p>
        ) : (
          <LineChart data={hours} height={240} labelEvery={2} />
        )}
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3>Pesquisas por dia (últimos {chart?.days || 14} dias)</h3>
          {dayTotal > 0 && peakDay.count > 0 && (
            <span className="chart-peak">Melhor dia: {peakDay.label} — {peakDay.count} pesquisa{peakDay.count === 1 ? '' : 's'}</span>
          )}
        </div>
        {dayTotal === 0 ? (
          <p className="chart-empty">Nenhuma pesquisa registrada ainda.</p>
        ) : (
          <LineChart data={days} height={270} labelEvery={1} showValues />
        )}
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3>Visitas da landing page (últimos {visitsChart?.days || 14} dias)</h3>
          {visitTotal > 0 && peakVisitDay.count > 0 && (
            <span className="chart-peak">Melhor dia: {peakVisitDay.label} — {peakVisitDay.count} visita{peakVisitDay.count === 1 ? '' : 's'}</span>
          )}
        </div>
        {visitTotal === 0 ? (
          <p className="chart-empty">Nenhuma visita registrada ainda.</p>
        ) : (
          <LineChart data={visitDays} height={270} labelEvery={1} showValues />
        )}
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3>Últimas pesquisas</h3>
        </div>
        <table>
          <thead>
            <tr>
              <th>Data / Hora</th>
              <th>Visitante</th>
              <th>Pesquisado</th>
              <th>Resultado</th>
              <th>Origem</th>
            </tr>
          </thead>
          <tbody>
            {searches.length === 0 && (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', color: 'var(--texto-secundario)', padding: 40 }}>
                  Nenhuma pesquisa registrada ainda.
                </td>
              </tr>
            )}
            {searches.map((s) => (
              <tr key={s.id}>
                <td>{formatDate(s.created_at)}</td>
                <td>{s.lead_name || '—'}</td>
                <td>{s.search_term}</td>
                <td><StatusBadge status={s.result} /></td>
                <td>{s.lead_name ? 'Formulário' : 'Busca'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
